import { ContractPricingFrequency } from "api/rest/rfp/brokerBidContractApi/types/ContractPricingFrequency";
import useLDFlag from "hooks/useLDFlag";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import BidRequestProgressionView from "../../components/BidRequestProgressionView";
import withBidRequest from "../../components/withBidRequest";
import useChangeBidRequestRoute from "../../hooks/useChangeBidRequestRoute";
import useUpdateContractRequest from "../../hooks/useUpdateContractRequest";
import { useUpdateBidRequest } from "../../store/bidRequestContractSlice";
import { useSetState, useSetStops } from "../../store/bidRequestFieldsSlice";
import { useBidRequestSliceSelector } from "../../store/bidRequestStore";
import BidRequestPageSteps from "../../types/BidRequestPageSteps";
import BidAttributesView from "./components/BidAttributesView";

const BidAttributesContainer = withBidRequest(() => {
  const bidRequestState = useBidRequestSliceSelector((state) => state.bidRequestContractSlice);
  const defaultHideInternalRefTillDispatch = useLDFlag("defaultHideInternalRefTillDispatch");

  const updateBidRequest = useUpdateBidRequest();
  const changeBidRequestRoute = useChangeBidRequestRoute();
  const setStops = useSetStops();
  const setState = useSetState();
  const stopsCount = useBidRequestSliceSelector((state) => state.bidRequestFieldsSlice.stopsCount);
  const { hidePoNumberTillDispatch, hideInternalRefTillDispatch } = useBidRequestSliceSelector(
    (state) => state.bidRequestFieldsSlice
  );
  const [updateContractRequest] = useUpdateContractRequest();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("common");

  const invalidPricingFrequency = !bidRequestState.pricing_frequency;
  const invalidContractDates =
    bidRequestState.pricing_frequency !== ContractPricingFrequency.BULK_SPOT_QUOTE &&
    (!bidRequestState.start || !bidRequestState.end);

  const shouldIncludeFuel =
    !!bidRequestState.pricing_frequency &&
    bidRequestState.pricing_frequency !== ContractPricingFrequency.BULK_SPOT_QUOTE;

  const invalidFuel = shouldIncludeFuel && bidRequestState.include_fuel === undefined;
  const invalidHazardous = bidRequestState.is_hazardous === undefined;
  const nextDisabled = invalidPricingFrequency || invalidContractDates || invalidFuel || invalidHazardous;

  const onNext = async () => {
    try {
      updateContractRequest();
      changeBidRequestRoute({ step: BidRequestPageSteps.Fields });
    } catch {
      enqueueSnackbar(t("errors.generic"), { variant: "error" });
    }
  };

  return (
    <BidRequestProgressionView
      fullPage
      onBack={() => changeBidRequestRoute({ step: BidRequestPageSteps.Trailer })}
      onNext={onNext}
      nextProps={{ disabled: nextDisabled }}
    >
      <BidAttributesView
        showFuelSection={shouldIncludeFuel}
        // extra details
        commodities={bidRequestState.commodities ?? ""}
        isHazardous={bidRequestState.is_hazardous}
        hazardousDetails={bidRequestState.hazardous_goods_details}
        includeFuel={bidRequestState.include_fuel}
        hidePoTillDispatch={hidePoNumberTillDispatch}
        hideInternalRefTillDispatch={Boolean(hideInternalRefTillDispatch || defaultHideInternalRefTillDispatch)}
        onChangeCommodities={(value) => updateBidRequest({ commodities: value })}
        onChangeHazardous={(value) => updateBidRequest({ is_hazardous: value })}
        onChangeFuel={(value) => updateBidRequest({ include_fuel: value })}
        onChangeHazardousDetails={(value) => updateBidRequest({ hazardous_goods_details: value })}
        onChangeHidePoTillDispatch={(val) => setState({ hidePoNumberTillDispatch: val })}
        onChangeHideInternalRefTillDispatch={(val) => setState({ hideInternalRefTillDispatch: val })}
        uploadExistingPricing={bidRequestState.is_existing_pricing}
        onChangeUploadExistingPricing={(val) => updateBidRequest({ is_existing_pricing: val })}
        // stop details
        numberOfStops={stopsCount}
        onChangeStops={(stopCount) => setStops({ stopCount, rfpPricingFrequency: bidRequestState.pricing_frequency })}
      />
    </BidRequestProgressionView>
  );
});

export default BidAttributesContainer;
